import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { loadGoogleMaps } from "@/lib/googleMaps";
import { renderSiteMarkers, siteInfoHtml } from "@/lib/siteMarkers";
import { Search, Navigation, Clock, RefreshCw, ExternalLink, MapPin, Flag, User } from "lucide-react";

const REFRESH_MS = 30000;

// Il fumetto della mappa si costruisce come stringa HTML, e targa, via e nome
// dell'autista arrivano dal satellitare: si passano da qui prima di infilarli
// dentro, cosi' un apostrofo o una parentesi angolare non rompono il riquadro.
function esc(testo) {
  return String(testo ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function statusOf(p) {
  if (p.ignition && p.speed > 3) return "moving";
  if (p.ignition) return "idle";
  return "off";
}
const STATUS_META = {
  moving: { label: "In movimento", color: "#10B981", badge: "bg-emerald-100 text-emerald-700" },
  idle: { label: "Accesso, fermo", color: "#F59E0B", badge: "bg-amber-100 text-amber-700" },
  off: { label: "Spento", color: "#94A3B8", badge: "bg-slate-200 text-slate-600" },
};

function timeAgo(ts) {
  if (!ts) return "—";
  const diffMin = Math.round((Date.now() / 1000 - Number(ts)) / 60);
  if (diffMin < 1) return "ora";
  if (diffMin < 60) return `${diffMin} min fa`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h} h fa`;
  return `${Math.floor(h / 24)} gg fa`;
}

export default function Localizzazione() {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markersRef = useRef({});
  const infoWindowRef = useRef(null);
  const listItemRefs = useRef({});
  const siteMarkersRef = useRef([]);
  const [positions, setPositions] = useState([]);
  const [sites, setSites] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);

  useEffect(() => {
    loadGoogleMaps().then(() => setMapsReady(true)).catch(() => setError("Impossibile caricare le mappe"));
  }, []);

  const load = useCallback(async (silent) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await api.get("/equipment/live-positions");
      setPositions(data.positions || []);
      setMeta({ count: data.count, total: data.total_devices });
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore nel recupero delle posizioni");
      if (!silent) toast.error("Errore localizzazione mezzi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    api.get("/dashboard/today-sites").then(({ data }) => setSites(data)).catch(() => {});
  }, []);

  // Inizializza la mappa una sola volta
  useEffect(() => {
    if (!mapsReady || !mapRef.current || mapObj.current) return;
    mapObj.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 45.34, lng: 9.35 }, zoom: 10,
      disableDefaultUI: true, zoomControl: true, streetViewControl: false,
    });
    infoWindowRef.current = new window.google.maps.InfoWindow();
  }, [mapsReady]);

  // Aggiorna i marker ad ogni cambio posizioni
  useEffect(() => {
    if (!mapObj.current || !window.google) return;
    const maps = window.google.maps;
    const seen = new Set();
    const bounds = new maps.LatLngBounds();

    positions.forEach(p => {
      if (!p.lat || !p.lon) return;
      seen.add(p.equipment_id);
      const status = statusOf(p);
      const color = STATUS_META[status].color;
      const pos = { lat: p.lat, lng: p.lon };
      bounds.extend(pos);

      const icon = {
        path: maps.SymbolPath.CIRCLE, scale: 8,
        fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2,
      };

      if (markersRef.current[p.equipment_id]) {
        markersRef.current[p.equipment_id].setPosition(pos);
        markersRef.current[p.equipment_id].setIcon(icon);
      } else {
        const marker = new maps.Marker({ position: pos, map: mapObj.current, icon, title: p.identifier });
        marker.addListener("click", () => {
          setSelected(p.equipment_id);
          if (infoWindowRef.current) {
            const statusLabel = STATUS_META[statusOf(p)].label;
            infoWindowRef.current.setContent(
              `<div style="font-family:sans-serif;padding:2px 4px;">
                 <div style="font-weight:700;font-size:13px;">${esc(p.identifier)}</div>
                 ${p.driver_name ? `<div style="font-size:11px;color:#0F172A;margin-top:2px;">Autista: <b>${esc(p.driver_name)}</b></div>` : ""}
                 <div style="font-size:11px;color:#64748B;margin-top:2px;">${esc(p.street || p.town || "Posizione non disponibile")}</div>
                 <div style="font-size:11px;color:#64748B;margin-top:2px;">${statusLabel} · ${p.speed} ${p.speed_unit}</div>
               </div>`
            );
            infoWindowRef.current.open(mapObj.current, marker);
          }
          const el = listItemRefs.current[p.equipment_id];
          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
        markersRef.current[p.equipment_id] = marker;
      }
    });

    Object.keys(markersRef.current).forEach(id => {
      if (!seen.has(id)) { markersRef.current[id].setMap(null); delete markersRef.current[id]; }
    });

    if (!bounds.isEmpty() && positions.length > 0 && !mapObj.current._fitted) {
      mapObj.current.fitBounds(bounds);
      mapObj.current._fitted = true;
    }
  }, [positions]);

  // Bandierine dei cantieri della giornata, così si vede dove sono i mezzi
  // rispetto ai posti dove si sta lavorando oggi.
  useEffect(() => {
    if (!mapsReady || !mapObj.current || !window.google) return undefined;
    let cancelled = false;
    siteMarkersRef.current.forEach(m => m.setMap(null));
    siteMarkersRef.current = [];
    renderSiteMarkers(window.google.maps, mapObj.current, sites, {
      onClick: (site, marker) => {
        if (!infoWindowRef.current) return;
        infoWindowRef.current.setContent(siteInfoHtml(site));
        infoWindowRef.current.open(mapObj.current, marker);
      },
    }).then(markers => {
      if (cancelled) { markers.forEach(m => m.setMap(null)); return; }
      siteMarkersRef.current = markers;
    });
    return () => { cancelled = true; };
  }, [sites, mapsReady]);

  const focusOn = (p) => {
    setSelected(p.equipment_id);
    if (mapObj.current && p.lat && p.lon) {
      mapObj.current.panTo({ lat: p.lat, lng: p.lon });
      mapObj.current.setZoom(15);
      const marker = markersRef.current[p.equipment_id];
      if (marker && infoWindowRef.current) {
        const statusLabel = STATUS_META[statusOf(p)].label;
        infoWindowRef.current.setContent(
          `<div style="font-family:sans-serif;padding:2px 4px;">
             <div style="font-weight:700;font-size:13px;">${esc(p.identifier)}</div>
             ${p.driver_name ? `<div style="font-size:11px;color:#0F172A;margin-top:2px;">Autista: <b>${esc(p.driver_name)}</b></div>` : ""}
             <div style="font-size:11px;color:#64748B;margin-top:2px;">${esc(p.street || p.town || "Posizione non disponibile")}</div>
             <div style="font-size:11px;color:#64748B;margin-top:2px;">${statusLabel} · ${p.speed} ${p.speed_unit}</div>
           </div>`
        );
        infoWindowRef.current.open(mapObj.current, marker);
      }
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = positions.filter(p =>
    !q
    || p.identifier.toLowerCase().includes(q)
    || (p.driver_name || "").toLowerCase().includes(q)
    || (p.street || "").toLowerCase().includes(q));

  const counts = {
    moving: positions.filter(p => statusOf(p) === "moving").length,
    idle: positions.filter(p => statusOf(p) === "idle").length,
    off: positions.filter(p => statusOf(p) === "off").length,
  };

  return (
    <div className="space-y-6" data-testid="localizzazione-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Mezzi e attrezzature</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Localizzazione mezzi</h1>
          <p className="text-sm text-slate-500 mt-1">
            {meta ? `${meta.count} mezzi collegati e riconosciuti` : "Caricamento..."}
            {" · aggiornamento automatico ogni 30s"}
          </p>
        </div>
        <button type="button" onClick={() => load()} className="flex items-center gap-2 text-sm text-orange-600 hover:underline">
          <RefreshCw className="w-4 h-4" /> Aggiorna ora
        </button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-700">{error}</CardContent>
        </Card>
      )}

      <div className="flex gap-3 flex-wrap">
        <Badge className="bg-emerald-100 text-emerald-700 border-0">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block mr-1.5" />
          In movimento: {counts.moving}
        </Badge>
        <Badge className="bg-amber-100 text-amber-700 border-0">
          <span className="w-2 h-2 rounded-full bg-amber-500 inline-block mr-1.5" />
          Accesi fermi: {counts.idle}
        </Badge>
        <Badge className="bg-slate-200 text-slate-600 border-0">
          <span className="w-2 h-2 rounded-full bg-slate-400 inline-block mr-1.5" />
          Spenti: {counts.off}
        </Badge>
        <Badge className="bg-blue-100 text-[#0067b2] border-0">
          <Flag className="w-3 h-3 mr-1.5" />
          Cantieri di oggi: {sites.length}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-slate-200 overflow-hidden">
          <div ref={mapRef} style={{ height: 520, width: "100%" }} />
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca targa, autista o via..." className="pl-9 h-10"
                     value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="space-y-2 max-h-[440px] overflow-y-auto">
              {loading && <p className="text-sm text-slate-500 text-center py-6">Caricamento posizioni...</p>}
              {!loading && filtered.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-6">Nessun mezzo trovato</p>
              )}
              {filtered.map(p => {
                const status = statusOf(p);
                const meta = STATUS_META[status];
                const isSel = selected === p.equipment_id;
                return (
                  <button key={p.equipment_id} type="button" onClick={() => focusOn(p)}
                          ref={(el) => { if (el) listItemRefs.current[p.equipment_id] = el; }}
                          className={`w-full text-left p-3 rounded border transition ${isSel ? "border-orange-400 bg-orange-50 ring-2 ring-orange-200" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-sm">{p.identifier}</span>
                      <Badge className={`${meta.badge} border-0 text-[10px]`}>{meta.label}</Badge>
                    </div>
                    {p.driver_name && (
                      <div className="text-xs text-slate-700 mt-1 truncate flex items-center gap-1">
                        <User className="w-3 h-3 flex-shrink-0 text-slate-400" />{p.driver_name}
                      </div>
                    )}
                    <div className="text-xs text-slate-500 mt-1 truncate flex items-center gap-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" />{p.street || p.town || "Posizione non disponibile"}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
                      <span className="flex items-center gap-1"><Navigation className="w-3 h-3" />{p.speed} {p.speed_unit}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(p.timestamp)}</span>
                    </div>
                    <Link to={`/attrezzature/${p.equipment_id}`}
                          className="text-[11px] text-orange-600 hover:underline inline-flex items-center gap-1 mt-1.5"
                          onClick={(e) => e.stopPropagation()}>
                      Scheda attrezzatura <ExternalLink className="w-3 h-3" />
                    </Link>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
